import { z } from 'zod';

/**
 * Zod schemas for configuration validation
 */

// Custom task pattern schema
const CustomTaskPatternSchema = z.object({
  name: z.string().min(1, 'Pattern name is required'),
  pattern: z.union([z.string(), z.instanceof(RegExp)]),
  priority: z.enum(['low', 'medium', 'high']).optional(),
  color: z.string().optional(),
});

// Task patterns schema
const TaskPatternsSchema = z.object({
  todo: z.boolean(),
  fixme: z.boolean(),
  hack: z.boolean(),
  note: z.boolean(),
  warning: z.boolean().optional(),
  optimize: z.boolean().optional(),
  custom: z.array(CustomTaskPatternSchema).optional(),
});

// Feature flags schema
const FeatureFlagsSchema = z.object({
  tasks: z.boolean(),
  ai: z.boolean(),
  realtime: z.boolean(),
  commandPalette: z.boolean(),
  fileEditing: z.boolean(),
  markdownPreview: z.boolean(),
});

// Auth provider schema
const AuthProviderSchema = z.object({
  id: z.string(),
  type: z.enum(['oauth', 'oidc', 'saml']),
  clientId: z.string(),
  clientSecret: z.string().optional(),
  issuer: z.string().optional(),
});

// Auth configuration schema
const AuthConfigSchema = z.object({
  jwtSecret: z.string().optional(),
  apiKey: z.string().optional(),
  session: z.object({
    secret: z.string(),
    maxAge: z.number().optional(),
  }).optional(),
  providers: z.array(AuthProviderSchema).optional(),
});

// Rate limit configuration schema
const RateLimitConfigSchema = z.object({
  windowMs: z.number().positive(),
  maxRequests: z.number().positive(),
  skipSuccessfulRequests: z.boolean().optional(),
  skipFailedRequests: z.boolean().optional(),
  tiers: z.object({
    anonymous: z.object({
      windowMs: z.number().positive(),
      maxRequests: z.number().positive(),
    }).optional(),
    authenticated: z.object({
      windowMs: z.number().positive(),
      maxRequests: z.number().positive(),
    }).optional(),
    premium: z.object({
      windowMs: z.number().positive(),
      maxRequests: z.number().positive(),
    }).optional(),
  }).optional(),
});

// CORS configuration schema
const CORSConfigSchema = z.object({
  allowedOrigins: z.union([
    z.array(z.string()),
    z.string(),
    z.function().args(z.string()).returns(z.boolean()),
  ]),
  allowedMethods: z.array(z.string()).optional(),
  allowedHeaders: z.array(z.string()).optional(),
  exposedHeaders: z.array(z.string()).optional(),
  credentials: z.boolean().optional(),
  maxAge: z.number().optional(),
});

// Security configuration schema
const SecurityConfigSchema = z.object({
  authentication: z.boolean(),
  auth: AuthConfigSchema.optional(),
  rateLimit: z.boolean(),
  rateLimitConfig: RateLimitConfigSchema.optional(),
  cors: z.boolean(),
  corsConfig: CORSConfigSchema.optional(),
  allowedPaths: z.array(z.string()).optional(),
  blockedPatterns: z.array(z.string()).optional(),
});

// UI configuration schema
const UIConfigSchema = z.object({
  theme: z.enum(['light', 'dark', 'system']).optional(),
  position: z.enum(['top-left', 'top-right', 'bottom-left', 'bottom-right']).optional(),
  defaultSize: z.object({
    width: z.union([z.number(), z.string()]).optional(),
    height: z.union([z.number(), z.string()]).optional(),
  }).optional(),
  hotkey: z.string().optional(),
  showInProduction: z.boolean().optional(),
  customCSS: z.string().optional(),
});

// API configuration schema
const APIConfigSchema = z.object({
  basePath: z.string(),
  version: z.string().optional(),
  documentation: z.boolean().optional(),
  middleware: z.array(z.string()).optional(),
  timeout: z.number().positive().optional(),
});

// Cache configuration schema
const CacheConfigSchema = z.object({
  enabled: z.boolean(),
  directory: z.string(),
  maxSize: z.number().positive(),
  ttl: z.number().positive(),
  strategy: z.enum(['lru', 'fifo', 'lfu']),
});

// Experimental configuration schema
const ExperimentalConfigSchema = z.object({
  markdownExtensions: z.boolean().optional(),
  customTaskProviders: z.boolean().optional(),
  plugins: z.boolean().optional(),
  pluginConfigs: z.record(z.any()).optional(),
});

// Main configuration schema
export const VibeshipConfigSchema = z.object({
  scanPaths: z.array(z.string()).min(1, 'At least one scan path is required'),
  include: z.array(z.string()).min(1, 'At least one include pattern is required'),
  exclude: z.array(z.string()),
  features: FeatureFlagsSchema,
  security: SecurityConfigSchema,
  taskPatterns: TaskPatternsSchema,
  ui: UIConfigSchema,
  api: APIConfigSchema,
  cache: CacheConfigSchema,
  experimental: ExperimentalConfigSchema.optional(),
});

// Partial configuration schema (for user input)
export const PartialVibeshipConfigSchema = VibeshipConfigSchema.deepPartial();

/**
 * Validate configuration with helpful error messages
 */
export function validateConfig(config: unknown): {
  success: boolean;
  data?: z.infer<typeof VibeshipConfigSchema>;
  errors?: string[];
} {
  try {
    const result = VibeshipConfigSchema.parse(config);
    return { success: true, data: result };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.map(err => {
        const path = err.path.join('.');
        const message = err.message;
        return `${path}: ${message}`;
      });
      return { success: false, errors };
    }
    return { 
      success: false, 
      errors: ['Invalid configuration format'] 
    };
  }
}

/**
 * Validate partial configuration
 */
export function validatePartialConfig(config: unknown): {
  success: boolean;
  data?: z.infer<typeof PartialVibeshipConfigSchema>;
  errors?: string[];
} {
  try {
    const result = PartialVibeshipConfigSchema.parse(config);
    return { success: true, data: result };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.map(err => {
        const path = err.path.join('.');
        const message = err.message;
        return `${path}: ${message}`;
      });
      return { success: false, errors };
    }
    return { 
      success: false, 
      errors: ['Invalid configuration format'] 
    };
  }
}

/**
 * Type guard for valid configuration
 */
export function isValidConfig(config: unknown): config is z.infer<typeof VibeshipConfigSchema> {
  return VibeshipConfigSchema.safeParse(config).success;
}

/**
 * Get configuration type from schema
 */
export type VibeshipConfigFromSchema = z.infer<typeof VibeshipConfigSchema>;